import { computed, effect, inject, untracked } from "@angular/core";

import { filter, pipe, switchMap, tap } from "rxjs";

import { AuthService } from "@core/services/auth.service";
import { LoggerService } from "@core/services/logger.service";
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { AiRole, AIRoleInstruction, CreateRoleRequest, UpdateRoleRequest } from "@shared/models/ai-role.model";
import { AIRolesHttpService } from "@shared/services/ai-roles-http.service";
import { ToastService } from "@shared/services/toast.service";

// State definition
interface RolesState {
  roles: AiRole[];
  loading: boolean;
  loaded: boolean;
  error: string | null;
  searchQuery: string;
  runningRoleId: string | null;
  updatedRole: { id: string; timestamp: number } | null;
}

const initialState: RolesState = {
  roles: [],
  loading: false,
  loaded: false,
  error: null,
  searchQuery: "",
  runningRoleId: null,
  updatedRole: null
};

export const RolesStore = signalStore(
  { providedIn: "root" },
  withState(initialState),
  withComputed(({ roles, searchQuery }) => ({
    filteredRoles: computed(() => {
      const query = searchQuery().toLowerCase().trim();
      if (!query) return roles();
      return roles().filter((r) => r.name.toLowerCase().includes(query));
    }),
    activeRole: computed(() => roles().find((r) => r.isActive) || null)
  })),
  withMethods(
    (store, rolesService = inject(AIRolesHttpService), toast = inject(ToastService), logger = inject(LoggerService)) => {
      const updateRoleState = (updatedRole: AiRole): void => {
        patchState(store, (state) => {
          // If we are activating this role, we need to deactivate others locally
          let newRoles = state.roles.map((r) => (r.id === updatedRole.id ? updatedRole : r));

          if (updatedRole.isActive) {
            newRoles = newRoles.map((r) => (r.id === updatedRole.id ? r : { ...r, isActive: false }));
          }

          return { roles: newRoles };
        });
      };

      return {
        reset(): void {
          patchState(store, initialState);
        },

        setSearchQuery(query: string): void {
          patchState(store, { searchQuery: query });
        },

        setRunningRoleId(roleId: string | null): void {
          patchState(store, { runningRoleId: roleId });
        },

        loadRoles: rxMethod<{ force?: boolean } | void>(
          pipe(
            filter((payload: { force?: boolean } | void) => {
              const force = !!(payload && typeof payload === "object" && payload.force);
              // If forcing, allow. If not, only allow if not already loaded and not currently loading.
              if (force) return true;
              return !store.loading() && !store.loaded();
            }),
            tap(() => patchState(store, { loading: true, error: null })),
            switchMap(() =>
              rolesService.getRoles(undefined, "desc").pipe(
                tap({
                  next: (roles) => patchState(store, { roles, loading: false, loaded: true }),
                  error: (err) => {
                    logger.error("Failed to load roles", err);
                    patchState(store, { error: "Failed to load roles", loading: false });
                  }
                })
              )
            )
          )
        ),

        createRole: rxMethod<CreateRoleRequest>(
          pipe(
            switchMap((role) =>
              rolesService.createRole(role).pipe(
                tap({
                  next: (createdRole) => {
                    patchState(store, (state) => ({ roles: [createdRole, ...state.roles] }));
                    toast.success("Role created successfully");
                  },
                  error: () => toast.error("Failed to create role")
                })
              )
            )
          )
        ),

        updateRole: rxMethod<{ id: string; data: UpdateRoleRequest }>(
          pipe(
            switchMap(({ id, data }) =>
              rolesService.updateRole(id, data).pipe(
                tap({
                  next: (updatedRole) => {
                    updateRoleState(updatedRole);
                  },
                  error: () => toast.error("Failed to update role")
                })
              )
            )
          )
        ),

        updateRoleState,

        deleteRole: rxMethod<string>(
          pipe(
            switchMap((id) =>
              rolesService.deleteRole(id).pipe(
                tap({
                  next: () => {
                    patchState(store, (state) => ({ roles: state.roles.filter((r) => r.id !== id) }));
                  },
                  error: () => toast.error("Failed to delete role")
                })
              )
            )
          )
        ),

        addInstruction: rxMethod<{ roleId: string; instruction: { text: string } }>(
          pipe(
            switchMap(({ roleId, instruction }) =>
              rolesService.addInstruction(roleId, instruction).pipe(
                tap({
                  next: (createdInstruction) => {
                    patchState(store, (state) => ({
                      roles: state.roles.map((r) =>
                        r.id === roleId ? { ...r, instructions: [...r.instructions, createdInstruction] } : r
                      )
                    }));
                  },
                  error: () => toast.error("Failed to add instruction")
                })
              )
            )
          )
        ),

        deleteInstruction: rxMethod<{ roleId: string; instructionId: string }>(
          pipe(
            switchMap(({ roleId, instructionId }) =>
              rolesService.deleteInstruction(instructionId).pipe(
                tap({
                  next: () => {
                    patchState(store, (state) => ({
                      roles: state.roles.map((role) =>
                        role.id === roleId
                          ? {
                              ...role,
                              instructions: role.instructions.filter((i) => i.id !== instructionId)
                            }
                          : role
                      )
                    }));
                    toast.success("Instruction deleted successfully");
                  },
                  error: () => toast.error("Failed to delete instruction")
                })
              )
            )
          )
        ),

        updateInstruction: rxMethod<{ instructionId: string; data: Partial<AIRoleInstruction> }>(
          pipe(
            switchMap(({ instructionId, data }) =>
              rolesService.updateInstruction(instructionId, data).pipe(
                tap({
                  next: (updatedInstruction) => {
                    patchState(store, (state) => ({
                      roles: state.roles.map((role) => ({
                        ...role,
                        instructions: role.instructions.map((inst) =>
                          inst.id === updatedInstruction.id ? updatedInstruction : inst
                        )
                      }))
                    }));
                  },
                  error: () => toast.error("Failed to update instruction")
                })
              )
            )
          )
        ),

        notifyRoleUpdated(id: string): void {
          patchState(store, { updatedRole: { id, timestamp: Date.now() } });
        }
      };
    }
  ),
  withHooks({
    onInit(store, authService = inject(AuthService)) {
      // Create a computed signal that only changes when the ID changes
      const activeUserId = computed(() => authService.currentUser()?.id);

      effect(() => {
        // Track user ID changes only
        activeUserId();

        // Reset store when user ID changes
        untracked(() => {
          patchState(store, initialState);
        });
      });
    }
  })
);
